import { FormControl, FormGroup } from '@angular/forms';
import { ISelectOption, isNullOrUndefined } from '@swissfex/sfx-commons';

import { BorrowerType } from '../../projects/models/enums/borrowerType';
import { IExtendedBorrowerAssetsFormGroup } from '../../projects/models/forms/IExtendedBorrowerAssetsFormGroup';
import { IExtendedBorrowerSharedAssetsFormGroup } from '../../projects/models/forms/IExtendedBorrowerSharedAssetsFormGroup';
import { IBorrower } from '../../projects/models/IBorrower';
import { IBorrowerNationalities } from '../../projects/models/IBorrowerNationalities';
import { IBorrowerNationality } from '../../projects/models/IBorrowerNationality';
import { IBorrowerOverview } from '../../projects/models/IBorrowerOverview';
import { IBorrowerPersonalInfo } from '../../projects/models/IBorrowerPersonalInfo';
import { IExtendedBorrower } from '../../projects/models/IExtendedBorrower';
import { IExtendedBorrowerAssets } from '../../projects/models/IExtendedBorrowerAssets';
import { IExtendedBorrowerSharedAssets } from '../../projects/models/IExtendedBorrowerSharedAssets';
import { IListableBorrower } from '../../projects/models/IListableBorrower';

export class BorrowerUtils {
    public static calculateOverallIncomeBasedOnBorrowerType(borrowersData: IBorrowerOverview): number {
        if (!borrowersData) {
            return null;
        }
        if (borrowersData.borrowersType === BorrowerType.Individual) {
            return this.calculateOverallIncome(borrowersData);
        } else {
            return borrowersData.legalEntity ? borrowersData.legalEntity.overallProfit : null;
        }
    }

    public static calculateOverallIncome(borrowersData: IBorrowerOverview): number {
        return this.sumIndividualBorrowerValues(borrowersData, (borrower) => borrower?.overallIncome);
    }

    public static calculateTotalAssets(borrowersData: IBorrowerOverview): number {
        return this.sumIndividualBorrowerValues(borrowersData, (borrower) => borrower?.calculated?.assetsTotal);
    }

    public static getIndividualBorrowerNames(borrowers: (IBorrowerPersonalInfo | IListableBorrower)[]): string[] {
        if (borrowers?.length) {
            return borrowers
                .filter((borrower) => borrower?.firstName || borrower?.lastName)
                .map((borrower) => `${borrower.firstName || ''} ${borrower.lastName || ''}`.trim());
        }

        return [];
    }

    public static getFullName(borrower: IBorrowerPersonalInfo): string {
        return `${borrower?.firstName || ''} ${borrower?.lastName || ''}`.trim();
    }

    public static getSelectedNationalityIds(nationalities: IBorrowerNationalities): number[] {
        if (!nationalities) {
            return [];
        }

        const nationalityIds: number[] = [];

        Object.values(nationalities).forEach((nationality: IBorrowerNationality) => {
            if (!isNullOrUndefined(nationality?.nationalityId)) {
                nationalityIds.push(nationality.nationalityId);
            }
        });

        return nationalityIds;
    }

    public static setupNationalityOptionsFromBorrowerNationalities(
        extendedBorrower: IExtendedBorrower
    ): ISelectOption<number>[] {
        if (!extendedBorrower) {
            return [];
        }

        const nationalityOptions: ISelectOption<number>[] = [];

        Object.values(extendedBorrower.personalInfo.nationalities).forEach((nationality: IBorrowerNationality) => {
            if (!isNullOrUndefined(nationality.nationalityId) && nationality.nationalityName) {
                nationalityOptions.push({
                    value: nationality.nationalityId,
                    label: nationality.nationalityName,
                    isDisabled: true
                });
            }
        });

        return nationalityOptions;
    }

    public static calculateNationalitiesSelectionLimit(nationalities: IBorrowerNationalities): number {
        if (!nationalities) {
            return null;
        }

        return Object.keys(nationalities).length;
    }

    private static sumIndividualBorrowerValues(
        borrowersData: IBorrowerOverview,
        selector: (borrower: IBorrower) => number
    ): number {
        if (borrowersData?.borrowersType !== BorrowerType.Individual) {
            return null;
        }

        const { firstBorrower, secondBorrower, thirdBorrower } = borrowersData;
        const v1 = selector(firstBorrower);
        const v2 = selector(secondBorrower);
        const v3 = selector(thirdBorrower);

        if (isNullOrUndefined(v1) && isNullOrUndefined(v2) && isNullOrUndefined(v3)) {
            return null;
        }

        return (v1 || 0) + (v2 || 0) + (v3 || 0);
    }

    public static getBorrowerSharedAssetsFormGroup(
        assets: IExtendedBorrowerSharedAssets,
        isDisabled?: boolean
    ): FormGroup<IExtendedBorrowerSharedAssetsFormGroup> {
        if (!assets) {
            return null;
        }

        const formGroup = new FormGroup<IExtendedBorrowerSharedAssetsFormGroup>({
            cash: new FormControl(assets.cash),
            securities: new FormControl(assets.securities)
        });

        if (isDisabled) {
            formGroup.disable();
        }

        return formGroup;
    }

    public static getBorrowerAssetsFormGroup(
        assets: IExtendedBorrowerAssets
    ): FormGroup<IExtendedBorrowerAssetsFormGroup> {
        if (!assets) {
            return null;
        }

        return new FormGroup<IExtendedBorrowerAssetsFormGroup>({
            ...this.getBorrowerSharedAssetsFormGroup(assets).controls,
            pillar3aBankAssets: new FormControl(assets.pillar3aBankAssets),
            buyBackValueLifeInsurancePolicies3ab: new FormControl(assets.buyBackValueLifeInsurancePolicies3ab),
            pillar2Assets: new FormControl(assets.pillar2Assets),
            otherFinancialAssets: new FormControl(assets.otherFinancialAssets),
            guaranteedLifeInsurancePolicies3ab: new FormControl(assets.guaranteedLifeInsurancePolicies3ab),
            vestedBenefitsCredit: new FormControl(assets.vestedBenefitsCredit)
        });
    }
}
